import { logEvent, type ProductEvent } from "@classdojo/log-client";
import cookies, { cookieJar, RAW_COOKIE } from "./cookies";
import { IS_TEST_SERVER } from "#/src/constants/client";
import type { FeatureName } from "#/src/hooks/useFeatureSwitches";
import { useOnFirstRender } from "@web-monorepo/hooks";

const getUtmMetadata = () => {
  const cookie = cookies.sourceUtmCampaigns;
  const cookieValue = cookieJar.get<string | undefined>(cookie.name, RAW_COOKIE);
  if (cookieValue) {
    return JSON.parse(cookieValue as string) as Record<string, unknown>;
  }
};

type TutorEventData =
  | string
  | (Parameters<typeof logEvent>[0] & {
      experiments?: FeatureName[];
      urlOverride?: string;
    });

const PRIVATE_QUERY_PARAMS = ["email", "parent_first_name", "parent_last_name"] as const;

export function sanitizeTutorEventUrl(url: string): string {
  let parsedUrl: URL;
  try {
    parsedUrl = new URL(url);
  } catch (error) {
    if (error instanceof TypeError) return `${window.location.origin}${window.location.pathname}`;
    throw error;
  }
  PRIVATE_QUERY_PARAMS.forEach((param) => parsedUrl.searchParams.delete(param));
  const pageUrl = `${parsedUrl.origin}${parsedUrl.pathname}${parsedUrl.search}`;
  const [hashPath, hashQuery] = parsedUrl.hash.split("?");
  if (hashQuery == null) return `${pageUrl}${parsedUrl.hash}`;

  const sanitizedQuery = new URLSearchParams(hashQuery);
  PRIVATE_QUERY_PARAMS.forEach((param) => sanitizedQuery.delete(param));
  const remainingQuery = sanitizedQuery.toString();
  return `${pageUrl}${hashPath ?? ""}${remainingQuery ? `?${remainingQuery}` : ""}`;
}

export function buildTutorEvent(
  event: ProductEvent,
  context: { campaignMetadata?: Record<string, unknown>; isTest: boolean; url: string },
): ProductEvent {
  return {
    eventName: event.eventName,
    eventValue: event.eventValue,
    userType: event.userType,
    entityId: event.entityId,
    experiments: event.experiments,
    metadata: {
      ...context.campaignMetadata,
      ...event.metadata,
      isTest: context.isTest,
      url: context.url,
    },
  };
}

export const logTutorEvent = async (rawData: TutorEventData): Promise<unknown> => {
  if (typeof rawData === "string") {
    return logTutorEvent({ eventName: rawData });
  }

  const eventUrl = sanitizeTutorEventUrl(
    rawData.urlOverride ?? window.location.origin + window.location.pathname + window.location.hash,
  );

  // Preserve immediate resolution for existing fire-and-forget callers. Starting
  // inside then also converts synchronous preparation/transport throws to rejections.
  setTimeout(() => {
    void Promise.resolve()
      .then(() =>
        logEvent(
          buildTutorEvent(rawData, { campaignMetadata: getUtmMetadata(), isTest: IS_TEST_SERVER, url: eventUrl }),
        ),
      )
      .catch(() => undefined);
  }, 0);
};

export const useLogTutorEventOnFirstRender = (event: TutorEventData) => {
  useOnFirstRender(() => {
    logTutorEvent(event);
  });
};
